import { usePageMeta, useSchemaOrg } from '@/hooks/usePageMeta';
import PageShell from '@/components/feature/PageShell';
import ProductCard from '@/components/feature/ProductCard';
import ArticleCard from '@/components/feature/ArticleCard';
import { products } from '@/mocks/products';
import { articles } from '@/mocks/articles';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { NEWSLETTER_FORM_URL } from '@/utils/forms';

const heroFeatures = [
  { icon: 'ri-shield-check-line', text: '产地直发 品质保证' },
  { icon: 'ri-truck-line', text: '顺丰冷链 新鲜送达' },
  { icon: 'ri-verified-badge-line', text: '严格筛选 颗颗精选' },
  { icon: 'ri-customer-service-2-line', text: '贴心售后 无忧购物' },
];

const advantages = [
  {
    icon: 'ri-map-pin-line',
    title: '核心产区',
    desc: '精选阳山、龙泉驿、奉化三大核心产区，独特地理环境孕育出最优质的蜜桃。',
  },
  {
    icon: 'ri-temp-hot-line',
    title: '严格品控',
    desc: '每一颗桃子从采摘到发货，经过5道筛选工序，只保留最完美的果实送到您手中。',
  },
  {
    icon: 'ri-flight-takeoff-line',
    title: '极速冷链',
    desc: '清晨采摘，当日发货。全程顺丰冷链运输，确保蜜桃以最佳状态新鲜送达。',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: '自然健康',
    desc: '坚持无公害种植，不使用催熟剂和保鲜剂。每一颗都是大自然最纯粹的馈赠。',
  },
];

const faqItems = [
  {
    q: '水蜜桃什么时候发货？',
    a: '我们坚持清晨采摘、当日发货的原则。每天上午10点前的订单当日发出，10点后的订单次日清晨采摘后发出。全程顺丰冷链运输，确保新鲜直达。',
  },
  {
    q: '收到的桃子太硬怎么办？',
    a: '为保证运输过程中的完整性，我们会选择八成熟左右的桃子发货。收到后请在室温下放置1-3天，桃子会自然变软、散发浓郁香气，此时口感最佳。',
  },
  {
    q: '水蜜桃可以保存多久？',
    a: '常温下完全成熟的桃子建议2天内食用完毕。如需延长保存，可将软桃放入冰箱冷藏（2-4℃），可保存3-5天。未成熟的硬桃请勿冷藏，先在室温下放软。',
  },
  {
    q: '你们的桃子产自哪里？',
    a: '我们的水蜜桃精选自中国三大核心产区：江苏无锡阳山（火山灰土壤，果大汁多）、四川成都龙泉驿（日照充足，细腻柔软）、浙江宁波奉化（千年栽培历史，皮薄味甜）。',
  },
  {
    q: '可以开发票吗？',
    a: '可以的。下单时在备注中注明开票信息（公司名称、税号），我们会在发货后3个工作日内为您开具电子发票并发送至您的邮箱。',
  },
  {
    q: '礼盒装和普通装有什么区别？',
    a: '礼盒装选用单果250g以上的特级果，果形端正饱满、色泽更加红润，搭配精美礼盒和手提袋，适合送礼。普通装果重在200g左右，包装简约，适合家庭自用，性价比更高。',
  },
];

export default function Home() {
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [newsletterMsg, setNewsletterMsg] = useState('');

  usePageMeta({
    title: '美丽的小蜜桃——鲜嫩甜蜜的优选精品桃 | 优质水蜜桃产地直发',
    description: '美丽的小蜜桃，外形饱满圆润、色泽红润诱人，口感鲜嫩多汁、甜蜜清香。精选优质产区，严格把控品质，精品桃子采购、新鲜蜜桃礼盒、优质水蜜桃产地直发。',
    keywords: '美丽的小蜜桃,小蜜桃哪里买,新鲜蜜桃礼盒,优质水蜜桃产地直发,甜蜜桃子推荐,精品桃子采购,水蜜桃礼盒装',
    canonicalUrl: 'https://meilixiaomitao.com/',
  });

  useSchemaOrg({
    organization: true,
    website: true,
    webpage: true,
    faq: faqItems.map((item) => ({ question: item.q, answer: item.a })),
  });

  const handleNewsletter = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setNewsletterStatus('success');
      setNewsletterMsg('订阅成功！感谢您的关注。');
      setTimeout(() => setNewsletterStatus('idle'), 3000);
      return;
    }

    setNewsletterStatus('loading');
    try {
      const formUrl = NEWSLETTER_FORM_URL;
      const formData = new FormData(form);
      const resp = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (resp.ok && parsed?.code === 'OK') {
        setNewsletterStatus('success');
        setNewsletterMsg('订阅成功！感谢您的关注，我们会不定期发送新鲜蜜桃资讯。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        setNewsletterStatus('error');
        setNewsletterMsg(serverMsg || '订阅失败，请稍后再试。');
      }
    } catch {
      setNewsletterStatus('error');
      setNewsletterMsg('网络异常，请检查网络后重试。');
    }
    setTimeout(() => setNewsletterStatus('idle'), 4000);
  };

  const featuredProducts = products.slice(0, 4);
  const featuredArticles = articles.slice(0, 3);

  return (
    <PageShell>
      {/* Hero Section */}
      <section className="relative w-full h-[600px] md:h-[680px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20beautiful%20artistic%20composition%20of%20ripe%20pink%20peaches%20with%20dew%20drops%20on%20a%20soft%20cream%20textured%20background%2C%20warm%20golden%20morning%20light%2C%20delicate%20peach%20blossom%20branches%20in%20foreground%2C%20elegant%20minimalist%20food%20photography%2C%20soft%20bokeh%2C%20dreamy%20aesthetic%2C%20natural%20organic%20texture&width=1920&height=900&seq=hero-peach-01&orientation=landscape"
          alt="美丽的小蜜桃"
          title="美丽的小蜜桃——鲜嫩甜蜜的优选精品桃"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6">
          <p className="text-background-50/80 text-sm md:text-base font-medium tracking-widest mb-4 animate-fade-in">
            鲜嫩甜蜜 · 产地直发
          </p>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 text-center mb-5 max-w-3xl leading-tight animate-slide-up">
            美丽的小蜜桃
          </h1>
          <p className="text-background-50/85 text-base md:text-lg text-center max-w-xl mb-8 leading-relaxed animate-slide-up-delay">
            外形饱满圆润、色泽红润诱人<br className="hidden sm:block" />
            口感鲜嫩多汁、甜蜜清香
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4 animate-slide-up-delay-2">
            <Link
              to="/categories"
              className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
            >
              立即选购
            </Link>
            <Link
              to="/about"
              className="px-8 py-3.5 rounded-full border border-background-50/40 text-background-50 text-sm md:text-base font-medium hover:bg-background-50/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              了解品牌
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="max-w-5xl mx-auto px-4 md:px-6 pb-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {heroFeatures.map((f) => (
                <div key={f.text} className="flex items-center gap-2 bg-background-50/15 backdrop-blur-sm rounded-lg px-3 py-2.5 md:px-4 md:py-3">
                  <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center flex-shrink-0">
                    <i className={`${f.icon} text-background-50 text-sm md:text-base`}></i>
                  </div>
                  <span className="text-background-50 text-xs md:text-sm font-medium whitespace-nowrap">{f.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              精选推荐
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              人气爆品 · 甜蜜之选
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
              精选三大核心产区优质水蜜桃，每一颗都经过严格筛选，保证最佳品质
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6" data-product-shop>
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} {...product} />
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/categories"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-all duration-300 cursor-pointer"
            >
              查看全部产品
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Advantages */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
              为什么选择我们
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              用心做好每一颗蜜桃
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
              从产地到餐桌，我们用心把控每一个环节
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((item) => (
              <div key={item.title} className="bg-background-50 rounded-lg p-6 text-center border border-background-200 hover:border-primary-200 transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-100">
                  <i className={`${item.icon} text-2xl text-primary-600`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Counter */}
      <section className="py-14 md:py-16 px-4 md:px-6 bg-primary-500">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { number: '10万+', label: '累计客户' },
              { number: '500吨+', label: '年销售量' },
              { number: '99.6%', label: '好评率' },
              { number: '3大', label: '核心产区' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50 mb-1 font-heading">
                  {stat.number}
                </div>
                <div className="text-sm text-background-50/80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium mb-4">
              蜜桃知识
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              了解更多蜜桃故事
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
              选购指南、保存技巧、产区知识，关于蜜桃的一切这里都有
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredArticles.map((article) => (
              <ArticleCard key={article.id} {...article} />
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-secondary-300 text-secondary-700 text-sm font-medium hover:bg-secondary-50 transition-all duration-300 cursor-pointer"
            >
              阅读更多文章
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
              常见问题
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              您可能想问的
            </h2>
          </div>
          <div className="space-y-3">
            {faqItems.map((item, index) => (
              <details key={index} className="group bg-background-50 rounded-lg border border-background-200 overflow-hidden cursor-pointer">
                <summary className="flex items-center justify-between px-5 py-4 text-sm md:text-base font-medium text-foreground-800 list-none">
                  <span>{item.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-3">
                    <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                  </div>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter + CTA */}
      <section className="py-16 md:py-20 px-4 md:px-6 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20dreamy%20peach%20blossom%20branches%20against%20a%20warm%20cream%20background%2C%20delicate%20pink%20petals%2C%20elegant%20minimalist%20botanical%20illustration%20style%2C%20soft%20diffused%20light%2C%20romantic%20spring%20atmosphere%2C%20fine%20art%20photography%2C%20gentle%20pastel%20tones&width=1920&height=600&seq=cta-peach-01&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top opacity-60"
        />
        <div className="absolute inset-0 bg-background-50/70"></div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            订阅蜜桃新鲜资讯
          </h2>
          <p className="text-foreground-600 text-sm md:text-base mb-8">
            第一时间获取新品上市、时令限定和优惠活动信息
          </p>

          {newsletterStatus === 'success' ? (
            <div className="bg-green-50 border border-green-200 text-green-700 px-5 py-3 rounded-lg text-sm">
              {newsletterMsg}
            </div>
          ) : newsletterStatus === 'error' ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-3 rounded-lg text-sm">
              {newsletterMsg}
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleNewsletter}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="email"
                name="email"
                required
                placeholder="请输入您的邮箱地址"
                className="flex-1 px-5 py-3 rounded-full border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              />
              <input
                type="text"
                name="phone_alt"
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <button
                type="submit"
                disabled={newsletterStatus === 'loading'}
                className="px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {newsletterStatus === 'loading' ? '提交中...' : '立即订阅'}
              </button>
            </form>
          )}
        </div>
      </section>
    </PageShell>
  );
}